import React, { useState } from "react";
import { PageHero } from "../components/PageHero";
import { MapPin, Phone, Mail, Clock, ChevronDown, ChevronUp } from "lucide-react";

const WaIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="#22c55e">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.978-1.413A9.953 9.953 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.95 7.95 0 0 1-4.076-1.12l-.292-.173-3.032.861.872-3.018-.19-.31A7.96 7.96 0 0 1 4 12c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8z" />
  </svg>
);

const faqs = [
  {
    q: "What age groups do you accept?",
    a: "We welcome children from 1 to 6 years across our Playgroup (1–2 yrs), Nursery (2–3 yrs), Kindergarten (3–5 yrs), and Daycare (1–5 yrs) programs.",
  },
  {
    q: "What are the school timings?",
    a: "Playgroup: 9:00 am – 12:00 pm | Nursery: 9:00 am – 1:00 pm | Kindergarten: 9:00 am – 3:00 pm | Daycare: 8:00 am – 6:00 pm. All programs run Monday to Friday.",
  },
  {
    q: "Do you provide meals?",
    a: "Our Daycare program includes a nutritious mid-day meal and an evening snack. For other programs, children are encouraged to bring a healthy snack from home.",
  },
  {
    q: "How do I enrol my child?",
    a: "Simply submit an enquiry online or give us a call. We'll invite you for a free school visit, after which you can complete the admission formalities with our team.",
  },
  {
    q: "Is school transport available?",
    a: "We do not currently provide school transport, but we are happy to connect you with trusted local transport partners serving your area.",
  },
  {
    q: "Can I visit the school before enrolling?",
    a: "Absolutely! We encourage all parents to come for a free guided tour. Call us or WhatsApp to book a visit at a time that suits you.",
  },
];

export default function Contact() {
  const [openFaq, setOpenFaq] = useState(null);

  const handleEmailClick = () => {
    window.open(
      "https://mail.google.com/mail/?view=cm&to=springKids.edu@gmail.com&su=Enquiry from Spring Kids Website",
      "_blank"
    );
  };

  return (
    <div data-testid="page-contact">
      <PageHero title="Contact Us" breadcrumb="Contact" />

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10">
          {/* Left: info + map */}
          <div>
            <span className="section-label">Get In Touch</span>
            <div className="mt-6 kid-card space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-kid-lilac flex items-center justify-center text-white shrink-0">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="font-semibold text-kid-navy text-lg">#269, 1st Cross, Beside Brigade Omega Apartment</p>
                  <p className="text-kid-navy/70">Banashankari 6th Stage 1st Block, Bangalore - 560 061</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-kid-amber flex items-center justify-center text-white shrink-0">
                  <Phone size={20} />
                </div>
                <p className="font-semibold text-kid-navy text-lg self-center">9845025709</p>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-kid-amber flex items-center justify-center text-white shrink-0">
                  <Mail size={20} />
                </div>
                <p className="font-semibold text-kid-navy text-lg self-center">springKids.edu@gmail.com</p>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#E6F8E4] flex items-center justify-center text-emerald-600 shrink-0">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="font-semibold text-kid-navy">Office Hours</p>
                  <p className="text-kid-navy/70 text-sm mt-1">Monday – Friday: 8:00 am – 6:00 pm</p>
                  <p className="text-kid-navy/70 text-sm">Saturday: 9:00 am – 1:00 pm</p>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden">
                <iframe
                  title="Spring Kids Location"
                  src="https://maps.google.com/maps?q=Banashankari+6th+Stage+1st+Block+Bangalore+560061&output=embed&z=15"
                  className="w-full h-52 border-0"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </div>

          {/* Right: email + whatsapp */}
          <div className="flex flex-col gap-6">
            <div className="kid-card flex flex-col items-center text-center gap-5">
              <div className="w-20 h-20 rounded-full bg-kid-lilac flex items-center justify-center text-white">
                <Mail size={36} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-kid-navy">We'd Love to Hear from You!</h3>
                <p className="text-kid-navy/60 mt-3 text-base leading-relaxed">
                  Have a question about our programs, admissions, or anything else?
                  Send us an email — we'll get back to you shortly.
                </p>
              </div>
              <button
                onClick={handleEmailClick}
                className="btn-lilac flex items-center gap-2 text-base px-8 py-3 w-full justify-center"
                data-testid="contact-email-btn"
              >
                <Mail size={18} />
                Send us an Email
              </button>
              <p className="text-kid-navy/40 text-sm -mt-2">Opens Gmail in a new tab</p>
            </div>

            <a
              href="https://wa.me/919845025709?text=Hi%2C%20I%20would%20like%20to%20know%20more%20about%20Spring%20Kids%20Preschool"
              target="_blank"
              rel="noopener noreferrer"
              className="kid-card flex items-center gap-5 hover:shadow-md transition-shadow no-underline"
              data-testid="contact-whatsapp-btn"
            >
              <div className="w-16 h-16 rounded-full bg-[#E6F8E4] flex items-center justify-center shrink-0">
                <WaIcon />
              </div>
              <div>
                <p className="font-bold text-kid-navy text-lg">Chat on WhatsApp</p>
                <p className="text-kid-navy/60 text-sm mt-1">Quick replies · Mon – Sat, 9 am – 6 pm</p>
              </div>
              <span className="ml-auto text-emerald-500 font-semibold text-xl">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F3F4FC] py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="section-label">Common Questions</span>
            <h2 className="mt-2 text-3xl font-bold text-kid-navy">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="kid-card p-0 overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  className="w-full flex items-center justify-between px-6 py-4 text-left gap-4"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-kid-navy">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp size={18} className="text-kid-amber shrink-0" />
                    : <ChevronDown size={18} className="text-kid-amber shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-kid-navy/70 text-sm leading-relaxed border-t border-kid-navy/5 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
